import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import './setting.css'
import PlanTab from './plan-tab'
import ReadingTab, { readingSectionId } from './ReadingTab'
import { membershipNameType, settingTabsName } from 'utils/constant'
import { LogoutIcon } from '../../containers/icons/icons'
import { isEnableStreaking } from 'utils/helperFunction'
import { AppUseSelector } from '../../redux/hooks'
import { useLocation, useNavigate } from 'react-router-dom'
import { SETTING, STORIES } from 'routes/routeConstant'
import StoryDifficulty from 'components/account/StoryDifficulty'
import Streaks from 'components/account/Streaks'
import MyAccount from 'components/account/MyAccount'
import Integrations from 'components/account/Integrations'
import { logout } from '../../redux/actions/userAction'

// `key` is the hash-routed tab name; the labels are translation keys.
const settingGroups = [
  {
    id: 'learning',
    label: 'settings:groups.learning',
    items: [
      { key: settingTabsName.storyDifficulty, label: 'settings:pages.storyDifficulty' },
      { key: settingTabsName.reading, label: 'settings:pages.reading' },
      { key: settingTabsName.streaks, label: 'settings:pages.streaks' },
    ],
  },
  {
    id: 'account',
    label: 'settings:groups.account',
    items: [
      { key: settingTabsName.account, label: 'settings:pages.account' },
      { key: settingTabsName.plan, label: 'settings:pages.plan' },
      { key: settingTabsName.integrations, label: 'settings:pages.integrations' },
    ],
  },
]

// Character, audio and words used to be pages of their own. Their links (emails, the Words page)
// still work: they open Reading and scroll to that section.
const readingSectionHashes = [settingTabsName.character, settingTabsName.audio, settingTabsName.words, settingTabsName.discussion]

const pageKeys = settingGroups.flatMap((group) => group.items.map((item) => item.key))

const Setting = () => {
  const { t } = useTranslation('settings')
  const location = useLocation()
  const navigate = useNavigate()
  const { membership } = AppUseSelector(({ user }) => user)
  const { name } = membership || {}
  const isEnableStreak = useMemo(() => isEnableStreaking(name), [name])

  const hashKey = location.hash.replace('#', '')
  const isReadingSection = readingSectionHashes.includes(hashKey)
  const requestedPage = isReadingSection
    ? settingTabsName.reading
    : pageKeys.includes(hashKey) && (hashKey !== settingTabsName.streaks || isEnableStreak)
      ? hashKey
      : null
  // Phones show the list on a bare /setting and a page once one is chosen. Wider screens show
  // both, so a bare /setting opens Profile beside the list.
  const activeTab = requestedPage || settingTabsName.account

  useEffect(() => {
    if (membership?.name === membershipNameType.Teacher) {
      navigate(STORIES)
    }
  }, [membership?.name])

  useEffect(() => {
    if (isReadingSection) {
      document.getElementById(readingSectionId(hashKey))?.scrollIntoView({ block: 'start' })
    }
  }, [hashKey])

  const openPage = (key: string) => {
    navigate(`${SETTING}#${key}`)
    window.scrollTo(0, 0)
  }

  return (
    <div className={`settings-account-root${requestedPage ? ' has-page' : ''}`}>
      <aside className='settings-nav'>
        <h4 className='m-h4 m-gray-1 settings-nav-title'>{t('title')}</h4>
        <nav aria-label={t('title')}>
          {settingGroups.map((group) => (
            <div key={group.id} className='settings-nav-group'>
              <h2 className='settings-nav-group-label'>{t(group.label)}</h2>
              <ul className='settings-nav-list'>
                {group.items
                  .filter((item) => item.key !== settingTabsName.streaks || isEnableStreak)
                  .map((item) => (
                    <li key={item.key}>
                      <button
                        type='button'
                        className={`settings-nav-item${activeTab === item.key ? ' active' : ''}`}
                        aria-current={activeTab === item.key ? 'page' : undefined}
                        onClick={() => openPage(item.key)}
                      >
                        <span>{t(item.label)}</span>
                        <i className='fa fa-chevron-right settings-nav-chevron' aria-hidden='true' />
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
          <div className='settings-nav-divider' />
          <button type='button' className='settings-nav-item settings-nav-logout' onClick={() => logout(true)}>
            <LogoutIcon />
            <span>{t('logOut')}</span>
          </button>
        </nav>
      </aside>
      <main className='settings-account-main'>
        <button type='button' className='settings-back' onClick={() => navigate(SETTING)}>
          <i className='fa fa-chevron-left' aria-hidden='true' />
          {t('title')}
        </button>
        <div className={`settings-page${activeTab === settingTabsName.plan ? ' settings-page--wide' : ''}`}>
          {activeTab === settingTabsName.storyDifficulty && <StoryDifficulty />}
          {activeTab === settingTabsName.reading && <ReadingTab />}
          {activeTab === settingTabsName.streaks && isEnableStreak && (
            <div className='m-streak'>
              <Streaks />
            </div>
          )}
          {activeTab === settingTabsName.account && <MyAccount />}
          {activeTab === settingTabsName.plan && <PlanTab />}
          {activeTab === settingTabsName.integrations && <Integrations />}
        </div>
      </main>
    </div>
  )
}

export default Setting
